import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { formatMoney, isMoneyString } from '@tcrd/shared';
import { Button, Card, Notice, SelectField, TextArea, TextField } from '@tcrd/ui';
import { api } from '../../../lib/api';
import { fromLocalInput, toLocalInput } from '../../../lib/format';
import type { RideRequestDto } from '../../../lib/types';
import type { VehicleDto } from '../vehicles/types';

export interface QuotePayload {
  amount: string;
  tolls?: string;
  includedServices?: string[];
  conditions?: string;
  validityMinutes?: number;
  vehicleId?: string;
  proposedScheduledAt?: string;
  billingMode?: string;
  periodStart?: string;
  periodEnd?: string;
  ridesIncluded?: number;
}

const SUGGESTED_INCLUDES = ['Espera de 15 min', 'Agua', 'Peajes incluidos', 'Ayuda con equipaje', 'Silla para niño'];

export function QuoteForm({ r, onSubmit, busy }: { r: RideRequestDto; onSubmit: (p: QuotePayload) => void; busy: boolean }) {
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api<VehicleDto[]>('/vehicles') });
  const fare = r.suggestedFare;
  const [amount, setAmount] = useState(r.offeredFare ?? fare?.total ?? '');
  const [tolls, setTolls] = useState('0');
  const [includes, setIncludes] = useState<string[]>([]);
  const [custom, setCustom] = useState('');
  const [conditions, setConditions] = useState('');
  const [validity, setValidity] = useState(String(r.quoteValidityMinutes ?? 30));
  const [vehicleId, setVehicleId] = useState('');
  const [propose, setPropose] = useState(false);
  const [proposed, setProposed] = useState(() => toLocalInput(new Date(r.scheduledAt ? new Date(r.scheduledAt).getTime() + 3600_000 : Date.now() + 3600_000)));
  // Recurrentes
  const [billingMode, setBillingMode] = useState('MONTHLY');
  const [periodStart, setPeriodStart] = useState(new Date().toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10));
  const [ridesIncluded, setRidesIncluded] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!isMoneyString(amount) || Number(amount) <= 0) return setError('Indica un precio válido');
    if (tolls && !isMoneyString(tolls)) return setError('Peaje inválido');
    setError(null);
    onSubmit({
      amount,
      tolls: tolls || undefined,
      includedServices: includes,
      conditions: conditions || undefined,
      validityMinutes: Number(validity) || undefined,
      vehicleId: vehicleId || undefined,
      proposedScheduledAt: propose ? fromLocalInput(proposed) : undefined,
      ...(r.mode === 'RECURRING' ? { billingMode, periodStart, periodEnd: periodEnd || undefined, ridesIncluded: ridesIncluded ? Number(ridesIncluded) : undefined } : {}),
    });
  };

  return (
    <Card className="p-5 flex flex-col gap-4">
      <h3 className="font-bold text-lg">Enviar cotización</h3>
      {fare && (
        <details className="rounded-2xl bg-[var(--surface-2)] px-4 py-3 text-sm">
          <summary className="cursor-pointer font-medium">Tarifa sugerida: {formatMoney(fare.total)} <span className="text-[var(--text-muted)] font-normal">({fare.km} km · {fare.minutes} min)</span></summary>
          <dl className="mt-2 grid grid-cols-2 gap-y-1">
            <dt>Base</dt><dd className="text-right">{formatMoney(fare.base)}</dd>
            <dt>Distancia{fare.roundTripMultiplier > 1 ? ' (×2)' : ''}</dt><dd className="text-right">{formatMoney(fare.distance)}</dd>
            <dt>Tiempo</dt><dd className="text-right">{formatMoney(fare.time)}</dd>
            {Number(fare.serviceSurcharge) > 0 && (<><dt>Suplemento servicio</dt><dd className="text-right">{formatMoney(fare.serviceSurcharge)}</dd></>)}
            {Number(fare.airportSurcharge) > 0 && (<><dt>Aeropuerto</dt><dd className="text-right">{formatMoney(fare.airportSurcharge)}</dd></>)}
            {Number(fare.nightSurcharge) > 0 && (<><dt>Nocturno</dt><dd className="text-right">{formatMoney(fare.nightSurcharge)}</dd></>)}
            {fare.minimumApplied && <dd className="col-span-2 text-xs text-[var(--text-muted)]">Se aplicó la tarifa mínima.</dd>}
          </dl>
        </details>
      )}
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Precio final (RD$)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <TextField label="Peajes incluidos (RD$)" inputMode="decimal" value={tolls} onChange={(e) => setTolls(e.target.value)} hint="Solo informativo" />
      </div>
      {isMoneyString(amount) && isMoneyString(tolls || '0') && Number(tolls) > 0 && (
        <p className="text-xs text-[var(--text-muted)] -mt-2">El cliente verá {formatMoney(amount)} con peajes por {formatMoney(tolls)} ya incluidos en ese total.</p>
      )}

      {r.mode === 'RECURRING' && (
        <div className="grid grid-cols-2 gap-3 rounded-2xl border border-[var(--border)] p-3">
          <SelectField label="Modalidad de cobro" value={billingMode} onChange={(e) => setBillingMode(e.target.value)}>
            <option value="PER_RIDE">Por viaje</option>
            <option value="WEEKLY">Semanal</option>
            <option value="MONTHLY">Mensual</option>
            <option value="PACKAGE">Paquete de viajes</option>
          </SelectField>
          <TextField label="Viajes incluidos" type="number" value={ridesIncluded} onChange={(e) => setRidesIncluded(e.target.value)} hint="Opcional" />
          <TextField label="Desde" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          <TextField label="Hasta" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          <p className="col-span-2 text-xs text-[var(--text-muted)]">{billingMode === 'PER_RIDE' ? 'El precio indicado se cobra por cada trayecto.' : 'El precio indicado cubre todo el período o paquete.'}</p>
        </div>
      )}

      <div>
        <p className="text-sm font-medium mb-2">Incluye</p>
        <div className="flex flex-wrap gap-2">
          {[...new Set([...SUGGESTED_INCLUDES, ...includes])].map((s) => {
            const on = includes.includes(s);
            return (
              <button key={s} type="button" aria-pressed={on} onClick={() => setIncludes((x) => (on ? x.filter((y) => y !== s) : [...x, s]))} className={`tap rounded-full border px-3 text-sm ${on ? 'border-brand-red bg-brand-red/10 font-medium' : 'border-[var(--border)]'}`}>
                {s}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex gap-2">
          <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Otro…" className="flex-1 h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3 text-sm" maxLength={80} />
          <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => custom.trim() && (setIncludes((x) => [...x, custom.trim()]), setCustom(''))}>Añadir</Button>
        </div>
      </div>

      <TextArea label="Condiciones" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Ej. Espera máxima 15 min; cargo adicional por paradas no previstas." />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Validez (minutos)" type="number" min={5} value={validity} onChange={(e) => setValidity(e.target.value)} />
        <SelectField label="Vehículo" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">Principal</option>
          {vehicles.data?.filter((v) => v.status === 'ACTIVE').map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} · {v.passengerCapacity} pax</option>)}
        </SelectField>
      </div>
      {r.mode !== 'RECURRING' && (
        <>
          <label className="tap flex items-center gap-3 text-sm">
            <input type="checkbox" className="size-5 accent-[#E5252A]" checked={propose} onChange={(e) => setPropose(e.target.checked)} /> Proponer otro horario
          </label>
          {propose && (
            <div className="flex items-center gap-2">
              <input type="datetime-local" value={proposed} onChange={(e) => setProposed(e.target.value)} className="flex-1 h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-4" aria-label="Horario propuesto" />
              <button className="tap flex items-center justify-center" onClick={() => setPropose(false)} aria-label="Quitar propuesta"><X className="size-5" /></button>
            </div>
          )}
        </>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
      <Button variant="accent" size="lg" loading={busy} onClick={submit}>Enviar cotización de {isMoneyString(amount) ? formatMoney(amount) : '—'}</Button>
    </Card>
  );
}
